/** Angular */
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
/** Services */
import { BydFeatureFlagService } from '../services/byd-feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class BydFeatureFlagsGuard implements CanActivate, CanLoad, CanActivateChild {
  /**
   * @param bydFeatureFlagService check the feature flag(s) state
   * @param featureFlagsKey the feature flags key in the environment file
   */
  constructor(
    private bydFeatureFlagService: BydFeatureFlagService // @Optional() @Inject(BYD_FEATURE_FLAGS_INJECTION_TOKEN) private featureFlagsKey?: string
  ) {}

  /**
   * Check if the route can be activated based on the feature flags,
   * from the associated route data.
   *
   * @param route contains the information about the associated route
   * @example RouterModule.forChild([
   *          {
                path: 'project',
                component: ProjectComponent,
                canActivate: [BydFeatureFlagsGuard],
                children: []
              }
            ])
   * @returns boolean
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.hasFlags(route);
  }

  /**
   * Check if just the `child` or both `child` & `parent` route can be activated based on the feature flags,
   * from the associated route data.
   *
   * @example child RouterModule.forChild([
   *          {
                path: 'project',
                component: ProjectComponent,
                children: [
                  {
                    path: '',
                    component: ProjectEditComponent,
                    canActivateChild: [BydFeatureFlagsGuard],
                      children: [
                        { path: 'edit', component: ProjectEditComponent },
                      ]
                  }
                ]
              }
            ])
   * @example both RouterModule.forChild([
            {
                path: 'project',
                component: ProjectComponent,
                canActivateChild: [BydFeatureFlagsGuard],// here is the guard on parent
                ...
              }
            ])
   * @param route contains the information about the associated route
   * @returns boolean
   */
  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.canActivate(route);
  }

  /**
   * Check if the route can be activated based on the feature flags,
   * from the associated route data.
   *
   * @param route contains the information about the associated route
   * @example RouterModule.forChild([
   *          {
                path: 'project',
                component: ProjectComponent,
                canLoad: [BydFeatureFlagsGuard],
                children: []
              }
            ])
   * @returns boolean
   */
  canLoad(route: Route): Observable<boolean> {
    return this.hasFlags(route);
  }

  /**
   *Check the feature flags key(s) precense & state in the run time environment file.
   *
   * @param route contains the information about the associated route
   * @returns boolean
   */
  private hasFlags(route: Route | ActivatedRouteSnapshot): Observable<boolean> {
    return of(this.bydFeatureFlagService.hasFlags(route.data[this.bydFeatureFlagService.featureFlagsKey]));
  }
}
